import { useCallback, useState } from "react";

/**
 * A value that follows `seed` until someone picks one: an untouched default
 * moves with it (a clinic zone that settles, a clock that ticks over) while a
 * pick stays put. `resetKey` drops the pick when it changes — a dialog
 * reopening, another record loading — and is compared during render, so the
 * seed shows on that same render.
 *
 * Memoize an object `seed` (or derive it from a primitive) so it is stable.
 */
export function useFollowingDefault<T>(
  seed: T,
  resetKey?: string | number | boolean | null,
): [
  value: T,
  pick: (value: T) => void,
  state: { edited: boolean; reset: () => void },
] {
  const [picked, setPicked] = useState<{ value: T } | null>(null);
  const [seenKey, setSeenKey] = useState(resetKey);
  let current = picked;
  if (resetKey !== seenKey) {
    setSeenKey(resetKey);
    setPicked(null);
    current = null;
  }
  const pick = useCallback((value: T) => setPicked({ value }), []);
  const reset = useCallback(() => setPicked(null), []);
  return [
    current ? current.value : seed,
    pick,
    { edited: current !== null, reset },
  ];
}
